/**
 * Document-group taxonomy for the applicant-web `documents` section
 * (Phase 7f).
 *
 * The documents section's catalog field ids follow a stable
 * convention: `document.<group>.<field>` (e.g.
 * `document.beneficiary_identity.passport_bio_page`). Phase 7f
 * sub-groups the section's 30-ish file-upload fields by what the
 * group *proves* so applicants can pace through clearly-named
 * clusters instead of one undifferentiated list.
 *
 * The group key is derived from the second segment of the field id;
 * the heading + intro come from `DOCUMENT_GROUP_METADATA`. Render
 * order is fixed in `DOCUMENT_GROUP_ORDER` so the page reads top-down
 * regardless of catalog declaration order.
 *
 * This file is pure data + a tiny derivation helper. The renderer
 * (SectionRenderer.tsx) consumes it.
 */

/**
 * Stable keys for the document sub-groups. Each value matches the
 * second segment of a `document.<group>.<field>` id.
 */
export type TDocumentGroupKey =
  | 'beneficiary_identity'
  | 'beneficiary_status'
  | 'children'
  | 'marriage'
  | 'sponsor_identity'
  | 'sponsor_financial'
  | 'screening';

/**
 * Per-group display metadata: a human-readable heading + a one-line
 * intro that explains what the group proves. Authoring rules mirror
 * the SECTION_INTROS guide:
 *   1. Plain second-person English.
 *   2. Name what the group proves and when an applicant can skip it.
 *   3. Stay short — the intro renders under the heading and shouldn't
 *      push the first upload widget off-screen.
 */
export interface IDocumentGroupMetadata {
  displayLabel: string;
  intro: string;
}

export const DOCUMENT_GROUP_METADATA: Readonly<
  Record<TDocumentGroupKey, IDocumentGroupMetadata>
> = {
  beneficiary_identity: {
    displayLabel: 'Your identity',
    intro:
      'Documents that prove who you are: birth certificate, passport, photo ID, USCIS-style photos.',
  },
  beneficiary_status: {
    displayLabel: 'Your current immigration status',
    intro:
      "Existing USCIS-issued documents (EAD, advance parole). Skip if you don't have any yet.",
  },
  children: {
    displayLabel: 'Your children',
    intro:
      "Birth certificates and passports for each child filing with you or whose information you're providing.",
  },
  marriage: {
    displayLabel: 'Your marriage',
    intro:
      'Marriage certificate plus evidence the marriage is genuine.',
  },
  sponsor_identity: {
    displayLabel: "Your sponsor's identity",
    intro:
      'Documents proving your sponsor is a U.S. citizen or lawful permanent resident.',
  },
  sponsor_financial: {
    displayLabel: "Your sponsor's finances",
    intro:
      'Evidence the sponsor meets the I-864 income threshold.',
  },
  screening: {
    displayLabel: 'Background & screening',
    intro:
      "Records USCIS may need depending on your history. Many applicants have nothing here — skip what doesn't apply.",
  },
};

/**
 * Render order for the document groups. The list follows the
 * applicant's mental model: identity first, then status, then family
 * (children, marriage), then sponsor (identity, finances), and
 * finally the screening tail (most applicants skip everything in it).
 *
 * Groups absent from a particular applicant's visible-field set are
 * filtered out by the renderer; the order here is the canonical
 * sequence regardless.
 */
export const DOCUMENT_GROUP_ORDER: readonly TDocumentGroupKey[] = [
  'beneficiary_identity',
  'beneficiary_status',
  'children',
  'marriage',
  'sponsor_identity',
  'sponsor_financial',
  'screening',
];

/**
 * Set of valid document-group keys, for quick membership tests in
 * `getDocumentGroupKey`.
 */
const DOCUMENT_GROUP_KEY_SET: ReadonlySet<string> = new Set(
  DOCUMENT_GROUP_ORDER,
);

/**
 * Returns the document-group key for a field id, or `null` for ids
 * that don't follow the `document.<group>.<field>` convention.
 *
 * Returns `null` (not the raw second segment) for unknown groups so
 * a typo or future field id added under a non-canonical group key
 * fails loudly in the renderer instead of rendering an unlabeled
 * heading.
 */
export function getDocumentGroupKey(
  fieldId: string,
): TDocumentGroupKey | null {
  if (!fieldId.startsWith('document.')) return null;
  const segments = fieldId.split('.');
  if (segments.length < 3) return null;
  const candidate = segments[1];
  if (!DOCUMENT_GROUP_KEY_SET.has(candidate)) return null;
  return candidate as TDocumentGroupKey;
}
